"use client";

/**
 * Shared SVG primitives for Financial Snapshot charts.
 * Keep this file focused on pure layout helpers — no chart-specific logic.
 */

import type { ReactNode } from "react";

export const CHART_HEIGHT = 220;
export const CHART_PADDING = { top: 16, right: 16, bottom: 28, left: 40 };

export function axisLabel(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toFixed(0);
}

export function pctLabel(value: number): string {
  return `${(value * 100).toFixed(0)}%`;
}

export function niceScale(rawMax: number, rawMin = 0): { min: number; max: number; ticks: number[] } {
  if (rawMax === rawMin) return { min: rawMin, max: rawMax + 1, ticks: [rawMin, rawMax + 1] };
  const range = rawMax - rawMin;
  const step = Math.pow(10, Math.floor(Math.log10(range))) / 2;
  const min = Math.floor(rawMin / step) * step;
  const max = Math.ceil(rawMax / step) * step;
  const ticks: number[] = [];
  for (let v = min; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(4)));
  return { min, max, ticks };
}

export function YAxis({
  ticks,
  scale,
  format,
  width,
}: {
  ticks: number[];
  scale: (v: number) => number;
  format: (v: number) => string;
  width: number;
}) {
  return (
    <g>
      {ticks.map((t) => {
        const y = scale(t);
        return (
          <g key={t}>
            <line
              x1={CHART_PADDING.left}
              x2={width - CHART_PADDING.right}
              y1={y}
              y2={y}
              stroke="currentColor"
              strokeOpacity={0.08}
            />
            <text
              x={CHART_PADDING.left - 6}
              y={y + 3}
              fontSize={10}
              textAnchor="end"
              fill="currentColor"
              fillOpacity={0.5}
              className="font-tabular"
            >
              {format(t)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export function XAxisLabels({
  labels,
  xScale,
  y,
}: {
  labels: string[];
  xScale: (i: number) => number;
  y: number;
}) {
  return (
    <g>
      {labels.map((l, i) => (
        <text
          key={`${l}-${i}`}
          x={xScale(i)}
          y={y}
          fontSize={10}
          textAnchor="middle"
          fill="currentColor"
          fillOpacity={0.6}
          className="font-tabular"
        >
          {l}
        </text>
      ))}
    </g>
  );
}

export function ChartSvg({ children, viewBox, className }: { children: ReactNode; viewBox: string; className?: string }) {
  return (
    <svg viewBox={viewBox} className={className} preserveAspectRatio="none" style={{ width: "100%", height: CHART_HEIGHT }}>
      {children}
    </svg>
  );
}
